import React, { useEffect, useState, useRef } from 'react';
import { getSessions, deleteSession } from '../../../services/analystSessionService';
import AddIcon from '@mui/icons-material/Add';
import TableChartIcon from '@mui/icons-material/TableChart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';

function timeAgo(date) {
  const seconds = Math.floor((Date.now() - new Date(date)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const AnalystGallery = ({ organizationId, onNewSession, onOpenSession, onRenameSession, onDeleteSession }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef(null);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;

    const fetchSessions = async () => {
      try {
        const res = await getSessions(organizationId);
        if (cancelled) return;
        setSessions(res.data?.data || []);
      } catch (err) {
        console.error('Failed to fetch analyst sessions:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSessions();
    return () => { cancelled = true; };
  }, [organizationId]);

  const handleDelete = async (e, session) => {
    e.stopPropagation();
    try {
      await deleteSession(organizationId, session._id);
      setSessions(prev => prev.filter(s => s._id !== session._id));
      if (onDeleteSession) onDeleteSession(session._id);
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const startEditing = (e, session) => {
    e.stopPropagation();
    setEditingId(session._id);
    setEditValue(session.name || '');
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const commitEditing = async () => {
    const trimmed = editValue.trim();
    const id = editingId;
    setEditingId(null);
    if (!trimmed || !id) return;
    const prev = sessions.find(s => s._id === id);
    if (prev && trimmed === prev.name) return;
    // Optimistic update
    setSessions(s => s.map(sess => sess._id === id ? { ...sess, name: trimmed } : sess));
    if (onRenameSession) {
      try { await onRenameSession(id, trimmed); }
      catch { setSessions(s => s.map(sess => sess._id === id ? { ...sess, name: prev?.name } : sess)); }
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Analyst Sessions</h2>
            <p className="text-xs text-gray-400 mt-0.5">Each session is a spreadsheet + AI thread</p>
          </div>
          <button
            onClick={onNewSession}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-pink text-white rounded-lg hover:bg-brand-pink/90 transition-colors"
          >
            <AddIcon style={{ fontSize: '18px' }} />
            New Session
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-brand-pink/10 flex items-center justify-center mx-auto mb-4">
              <TableChartIcon className="text-brand-pink" style={{ fontSize: '32px' }} />
            </div>
            <p className="text-sm text-gray-600 font-medium mb-1">No sessions yet</p>
            <p className="text-xs text-gray-400 mb-4">
              Start a new session to paste data and run analysis
            </p>
            <button
              onClick={onNewSession}
              className="px-4 py-2 text-sm bg-brand-pink text-white rounded-lg hover:bg-brand-pink/90 transition-colors"
            >
              Create your first session
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* New session card */}
            <button
              onClick={onNewSession}
              className="group h-32 rounded-xl border-2 border-dashed border-gray-200 hover:border-brand-pink/50 flex flex-col items-center justify-center gap-2 transition-colors"
            >
              <AddIcon className="text-gray-300 group-hover:text-brand-pink transition-colors" style={{ fontSize: '28px' }} />
              <span className="text-xs text-gray-400 group-hover:text-brand-pink transition-colors">New Session</span>
            </button>

            {/* Existing session cards */}
            {sessions.map(session => (
              <div
                key={session._id}
                onClick={() => onOpenSession(session)}
                className="group relative h-32 rounded-xl border border-gray-200 bg-white hover:shadow-md hover:border-brand-pink/30 cursor-pointer transition-all p-4 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <TableChartIcon className="text-[#404880]" style={{ fontSize: '16px' }} />
                    {editingId === session._id ? (
                      <input
                        ref={editInputRef}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={commitEditing}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitEditing();
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        className="text-sm font-medium text-gray-800 bg-gray-50 border border-gray-300 rounded px-1.5 py-0.5 outline-none focus:border-brand-pink focus:ring-1 focus:ring-brand-pink/30 w-full"
                        autoFocus
                      />
                    ) : (
                      <span
                        className="text-sm font-medium text-gray-800 truncate"
                        onDoubleClick={(e) => startEditing(e, session)}
                      >
                        {session.name || 'Untitled Session'}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400">
                    {session.sheets?.length || 0} sheet{(session.sheets?.length || 0) !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">{timeAgo(session.updated_at || session.created_at)}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-500">
                    {session.sheets?.filter(s => s.thread_id).length || 0} active
                  </span>
                </div>
                <button
                  onClick={(e) => startEditing(e, session)}
                  className="absolute top-2 right-8 opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-blue-50 text-gray-400 hover:text-brand-pink transition-all"
                  title="Rename session"
                >
                  <EditIcon style={{ fontSize: '16px' }} />
                </button>
                <button
                  onClick={(e) => handleDelete(e, session)}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
                  title="Delete session"
                >
                  <DeleteOutlineIcon style={{ fontSize: '16px' }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalystGallery;
